import React from "react";
import ReactDOM from "react-dom/client";
import { BrowserRouter } from "react-router-dom";
import "./index.css";
import "./suppressWarnings";
import App from "./App";
import ErrorBoundary from "./components/ErrorBoundary";

// ---------------------------------------------------------------------------
// Global handler: auto-reload on ChunkLoadError (stale JS chunks after deploy)
// ---------------------------------------------------------------------------
const RELOAD_KEY = "sacgpt:chunkReloadCount";
const MAX_RELOADS = 2;

function isChunkError(error) {
  const msg = error?.message || "";
  if (
    error?.name === "ChunkLoadError" ||
    msg.includes("Loading chunk") ||
    msg.includes("Loading CSS chunk") ||
    (error instanceof SyntaxError && msg.includes("Unexpected token '<'"))
  ) {
    return true;
  }
  // Minified TypeError from stale bundles (e.g. "s is not a function")
  if (error instanceof TypeError && /^.{1,2} is not a (function|constructor)/.test(msg)) {
    return true;
  }
  return false;
}

/**
 * Suppress harmless third-party errors that pollute monitoring
 * (e.g. browser extensions, analytics scripts).
 */
function isThirdPartyNoise(error) {
  if (!error) return false;
  const msg = error.message || "";
  // NotFoundError from third-party DOM manipulation (extensions, widgets)
  if (error.name === "NotFoundError" || msg.includes("NotFoundError")) {
    return true;
  }
  // ResizeObserver loop errors are harmless browser quirks
  if (msg.includes("ResizeObserver loop")) {
    return true;
  }
  return false;
}

function handleChunkError() {
  const count = Number(sessionStorage.getItem(RELOAD_KEY) || "0");
  if (count < MAX_RELOADS) {
    sessionStorage.setItem(RELOAD_KEY, String(count + 1));
    window.location.reload();
  }
}

// Reset counter on successful page load
window.addEventListener("load", () => {
  sessionStorage.removeItem(RELOAD_KEY);
});

// Catch unhandled errors (e.g. script loading failures)
window.addEventListener("error", (event) => {
  if (isThirdPartyNoise(event.error)) {
    event.preventDefault();
    return;
  }
  if (isChunkError(event.error)) {
    event.preventDefault();
    handleChunkError();
  }
});

// Catch unhandled promise rejections (e.g. lazy() / import() failures)
window.addEventListener("unhandledrejection", (event) => {
  if (isThirdPartyNoise(event.reason)) {
    event.preventDefault();
    return;
  }
  if (isChunkError(event.reason)) {
    event.preventDefault();
    handleChunkError();
  }
});
// ---------------------------------------------------------------------------

ReactDOM.createRoot(document.getElementById("root")).render(
  // Temporarily disabled StrictMode to prevent double rendering in development
  // <React.StrictMode>
    <ErrorBoundary>
      <BrowserRouter>
        <App />
      </BrowserRouter>
    </ErrorBoundary>
  // </React.StrictMode>
);
